import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  padding: 40px 20px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 111, 0, 0.1);
  border-top-color: #ff6f00;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingComponent = () => (
  <LoadingContainer>
    <LoadingSpinner />
  </LoadingContainer>
);

/**
 * HOC для захисту сторінок які потребують авторизації
 * Перенаправляє неавторизованих користувачів на головну сторінку
 */
const withAuthProtection = (WrappedComponent) => {
  return function WithAuthProtectionComponent(props) {
    const navigate = useNavigate();
    const { currentLanguage } = useLanguage();
    const { user, loading: authLoading } = useAuth();
    const { addToast } = useToast();
    const { t } = useTranslation();
    const [shouldRender, setShouldRender] = useState(false);
    const [hasShownToast, setHasShownToast] = useState(false);

    useEffect(() => {
      // Чекаємо поки AuthContext завершить завантаження
      if (authLoading) {
        return;
      }

      // Якщо користувач не авторизований - редиректимо
      if (!user) {
        console.log('⚠️ Користувач не авторизований - перенаправлення на головну');
        
        // Показуємо toast тільки один раз
        if (!hasShownToast) {
          addToast(t('auth.requiredPage'), 'warning');
          setHasShownToast(true);
        }
        
        navigate(`/${currentLanguage}`, { replace: true });
        setShouldRender(false);
        return;
      }

      // Користувач авторизований - дозволяємо рендер
      setShouldRender(true);
    }, [user, authLoading, navigate, currentLanguage, addToast, hasShownToast]);

    // Показуємо loader поки перевіряємо авторизацію
    if (authLoading || (!shouldRender && user)) {
      return <LoadingComponent />;
    }

    // Якщо не авторизований - не рендеримо (редирект вже виконаний)
    if (!user || !shouldRender) {
      return null;
    }

    // Все ок - рендеримо компонент
    return <WrappedComponent {...props} />;
  };
};

export default withAuthProtection;
